/* eslint-disable @typescript-eslint/no-explicit-any */
import { getMatchEquationText } from "../../helper/helper";
import { startsIn, type MatchVm } from "../tournamentPage/matchVm";

/** Arrow state shared by every embed layout. `onCycle` steps to the next match and wraps round at the end. */
export type EmbedNav = { index: number; total: number; onPrev: () => void; onNext: () => void; onCycle: () => void };


/** Match centre of the full site. Opens in a new tab: the widget lives in someone else's page. */
export const matchUrl = (matchId: string) => `${window.location.origin}${import.meta.env.BASE_URL || "/"}matches-details/${matchId}`;

/** "19.1" stays, a finished "20.0" becomes "20"; the "/max" part is dropped. */
export const oversText = (o?: string) => (o ?? "").split("/")[0].replace(/\.0$/, "");

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export const startLabel = (d: Date | null) => {
  if (!d) return "Date to be announced";
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${day} · ${time}`;
};

/** "MVP: Bhargav Patel", then "MVP: B. Patel", then "MVP: BP": whichever longest one fits the column. */
export const mvpOptions = (name: string): string[] => {
  const w = name.split(/\s+/).filter(Boolean);
  const out = [`MVP: ${w.join(" ")}`];
  if (w.length > 1) out.push(`MVP: ${w[0][0].toUpperCase()}. ${w[w.length - 1]}`);
  out.push(`MVP: ${initials(name)}`);
  return [...new Set(out)];
};

/** The two lines on the right of the card: what is happening, and a small supporting line (one or more spellings of it, longest first). */
export const infoFor = (vm: MatchVm, now: number): { main: string; sub: string[] } => {
  const raw: any = vm.raw;
  if (vm.bucket === "up") return { main: startsIn(vm.start, now).text, sub: [startLabel(vm.start)] };
  if (vm.bucket === "past") return { main: vm.resultTitle && vm.resultTitle !== "-" ? vm.resultTitle : "Result", sub: vm.potm ? mvpOptions(vm.potm) : [] };

  const innings: any[] = raw?.inningScorecardList ?? [];
  const inn = innings[innings.length - 1];
  const eq = getMatchEquationText(raw);
  // "<Team> won the toss and chose to Bat" is too long for this slot: "<CODE> chose to bat"
  const tossFull = (raw?.tossResult ?? "").trim();
  const tm = tossFull.match(/^(.*?)\s+won the toss and\s+(?:chose|elected|opted)\s+to\s+(\w+)/i);
  const tossTeam = tm ? [vm.a, vm.b].find((t) => t.name.trim().toLowerCase() === tm[1].trim().toLowerCase()) : undefined;
  const toss = tm ? `${tossTeam?.code ?? tm[1]} chose to ${tm[2].toLowerCase()}` : tossFull;
  const main = eq && eq !== "-" ? eq : toss || "Match in progress";

  const balls = inn ? (inn.over ?? 0) * 6 + (inn.ball ?? 0) : 0;
  const maxOvers = inn?.maxOvers ?? 0;
  const crr = inn && Number(inn.currentRunrate) > 0 ? Number(inn.currentRunrate) : balls ? ((inn?.runs ?? 0) * 6) / balls : null;
  const parts: string[] = [];
  if (crr != null) parts.push(`CRR ${crr.toFixed(2)}`);
  // maxOvers is sometimes bad data (e.g. 450); only trust a plausible overs limit
  if (crr != null && maxOvers > 0 && maxOvers <= 100) {
    if (innings.length >= 2) {
      const need = (innings[0]?.runs ?? 0) + 1 - (inn?.runs ?? 0);
      const left = maxOvers * 6 - balls;
      if (need > 0 && left > 0) parts.push(`RRR ${((need * 6) / left).toFixed(2)}`);
    } else {
      parts.push(`Proj ${Math.round(crr * maxOvers)}`);
    }
  }
  return { main, sub: parts.length ? [parts.join(" · ")] : [] };
};

